"use strict";

const { schedulePathForYear } = require("./scheduleArchive");
const {
  scheduleEventCatalogEntryForItemId,
} = require("./scheduleEventCatalog");
const { validateJstDateTime } = require("./scheduleDateTime");

const EVENT_ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const EVENT_SCHEDULED = "https://schema.org/EventScheduled";
const OFFLINE_EVENT_ATTENDANCE_MODE =
  "https://schema.org/OfflineEventAttendanceMode";
const ORGANIZER_TYPES = new Set(["Organization", "Person"]);
const OFFER_AVAILABILITIES = new Set([
  "https://schema.org/InStock",
  "https://schema.org/PreOrder",
  "https://schema.org/SoldOut",
]);

const requireNonEmptyString = (value, label) => {
  if (typeof value !== "string" || !value.trim()) {
    throw new Error(`Missing or invalid ${label}.`);
  }

  return value.trim();
};

const parseHttpsUrl = (value, label) => {
  const urlValue = requireNonEmptyString(value, label);
  let url;

  try {
    url = new URL(urlValue);
  } catch (error) {
    throw new Error(`Invalid ${label}.`);
  }

  if (url.protocol !== "https:") {
    throw new Error(`${label} must use HTTPS.`);
  }

  return url;
};

const validateLocation = (location, eventId) => {
  if (!location || typeof location !== "object") {
    throw new Error(`Missing location for ${eventId}.`);
  }
  if (!location.address || typeof location.address !== "object") {
    throw new Error(`Missing address for ${eventId}.`);
  }

  const address = location.address;
  const normalizedAddress = {
    "@type": "PostalAddress",
    streetAddress: requireNonEmptyString(
      address.streetAddress,
      `street address for ${eventId}`
    ),
    addressLocality: requireNonEmptyString(
      address.addressLocality,
      `address locality for ${eventId}`
    ),
    addressRegion: requireNonEmptyString(
      address.addressRegion,
      `address region for ${eventId}`
    ),
    addressCountry: requireNonEmptyString(
      address.addressCountry,
      `address country for ${eventId}`
    ),
  };

  if (normalizedAddress.addressCountry !== "JP") {
    throw new Error(`Address country for ${eventId} must be JP.`);
  }

  if (address.postalCode !== undefined) {
    normalizedAddress.postalCode = requireNonEmptyString(
      address.postalCode,
      `postal code for ${eventId}`
    );
  }

  return {
    "@type": "Place",
    name: requireNonEmptyString(location.name, `location name for ${eventId}`),
    address: normalizedAddress,
  };
};

const normalizeOrganizers = (organizers, eventId) => {
  if (organizers === undefined) {
    return [];
  }
  if (!Array.isArray(organizers) || !organizers.length) {
    throw new Error(`Missing organizers for ${eventId}.`);
  }

  const identities = new Set();

  return organizers.map((organizer) => {
    if (!organizer || typeof organizer !== "object") {
      throw new Error(`Invalid organizer for ${eventId}.`);
    }

    const type = requireNonEmptyString(
      organizer.type,
      `organizer type for ${eventId}`
    );
    const name = requireNonEmptyString(
      organizer.name,
      `organizer name for ${eventId}`
    );

    if (!ORGANIZER_TYPES.has(type)) {
      throw new Error(`Unsupported organizer type for ${eventId}.`);
    }

    const normalizedOrganizer = { "@type": type, name };
    if (organizer.url !== undefined) {
      normalizedOrganizer.url = parseHttpsUrl(
        organizer.url,
        `organizer URL for ${eventId}`
      ).href;
    }

    const identity = [
      normalizedOrganizer["@type"],
      normalizedOrganizer.name,
      normalizedOrganizer.url || "",
    ].join("\u001f");
    if (identities.has(identity)) {
      throw new Error(`Duplicate organizer for ${eventId}.`);
    }
    identities.add(identity);

    return normalizedOrganizer;
  });
};

const normalizeImages = ({
  imageKeys,
  imageUrlsByKey,
  itemId,
  siteOrigin,
}) => {
  if (!Array.isArray(imageKeys) || !imageKeys.length) {
    throw new Error(`Missing image keys for ${itemId}.`);
  }
  if (
    !imageUrlsByKey ||
    typeof imageUrlsByKey !== "object" ||
    Array.isArray(imageUrlsByKey)
  ) {
    throw new Error("Missing schedule image URL mapping.");
  }

  const imageUrls = imageKeys.map((imageKey) => {
    const normalizedImageKey = requireNonEmptyString(
      imageKey,
      `image key for ${itemId}`
    );
    const imageUrl = parseHttpsUrl(
      imageUrlsByKey[normalizedImageKey],
      `image URL for ${normalizedImageKey}`
    );

    if (
      imageUrl.origin !== siteOrigin.origin ||
      imageUrl.search ||
      imageUrl.hash ||
      !/\.jpg$/i.test(imageUrl.pathname)
    ) {
      throw new Error(
        `Schedule image URL must be a same-origin JPEG for ${normalizedImageKey}.`
      );
    }

    return imageUrl.href;
  });

  if (new Set(imageUrls).size !== imageUrls.length) {
    throw new Error(`Duplicate schedule image URL for ${itemId}.`);
  }

  return imageUrls;
};

const normalizeOffers = ({ ticketOffers, ticketUrl, eventId, startDate }) => {
  if (ticketOffers === undefined) {
    return [];
  }
  if (!Array.isArray(ticketOffers) || !ticketOffers.length) {
    throw new Error(`Missing ticket offers for ${eventId}.`);
  }

  const offerUrl = parseHttpsUrl(ticketUrl, `ticket URL for ${eventId}`).href;
  const offerNames = new Set();

  return ticketOffers.map((offer) => {
    if (!offer || typeof offer !== "object") {
      throw new Error(`Invalid ticket offer for ${eventId}.`);
    }

    const name = requireNonEmptyString(
      offer.name,
      `ticket offer name for ${eventId}`
    );
    if (offerNames.has(name)) {
      throw new Error(`Duplicate ticket offer name for ${eventId}.`);
    }
    offerNames.add(name);

    if (
      typeof offer.price !== "number" ||
      !Number.isFinite(offer.price) ||
      offer.price < 0
    ) {
      throw new Error(`Invalid ticket offer price for ${eventId}.`);
    }

    const priceCurrency = requireNonEmptyString(
      offer.priceCurrency,
      `ticket offer currency for ${eventId}`
    );
    if (priceCurrency !== "JPY") {
      throw new Error(`Ticket offer currency must be JPY for ${eventId}.`);
    }

    const normalizedOffer = {
      "@type": "Offer",
      name,
      url: offerUrl,
      price: offer.price,
      priceCurrency,
    };

    if (offer.validFrom !== undefined) {
      const validFrom = validateJstDateTime(
        offer.validFrom,
        `ticket offer validFrom for ${eventId}`
      );
      if (Date.parse(validFrom) >= Date.parse(startDate)) {
        throw new Error(
          `Ticket offer validFrom must be before startDate for ${eventId}.`
        );
      }
      normalizedOffer.validFrom = validFrom;
    }

    if (offer.availability !== undefined) {
      const availability = requireNonEmptyString(
        offer.availability,
        `ticket offer availability for ${eventId}`
      );
      if (!OFFER_AVAILABILITIES.has(availability)) {
        throw new Error(`Unsupported ticket availability for ${eventId}.`);
      }
      normalizedOffer.availability = availability;
    }

    return normalizedOffer;
  });
};

const validateScheduleContext = ({
  canonicalUrl,
  imageUrlsByKey,
  page,
  site,
  scheduleItems,
}) => {
  if (!page || typeof page !== "object") {
    throw new Error("Missing schedule page metadata.");
  }
  if (!site || typeof site !== "object") {
    throw new Error("Missing site metadata.");
  }
  if (!site.artist || typeof site.artist !== "object") {
    throw new Error("Missing schedule performer metadata.");
  }
  if (!Array.isArray(scheduleItems) || !scheduleItems.length) {
    throw new Error("Schedule structured data requires at least one item.");
  }

  requireNonEmptyString(page.title, "schedule page title");
  requireNonEmptyString(page.description, "schedule page description");
  const hasScheduleItemId = Object.prototype.hasOwnProperty.call(
    page,
    "scheduleItemId"
  );
  const scheduleEvent = hasScheduleItemId
    ? scheduleEventCatalogEntryForItemId(page.scheduleItemId)
    : null;

  if (hasScheduleItemId && !scheduleEvent) {
    throw new Error(`Unknown schedule item page: ${page.scheduleItemId}`);
  }
  if (scheduleEvent && scheduleEvent.year !== page.scheduleYear) {
    throw new Error(
      `Schedule item year mismatch for ${page.scheduleItemId}.`
    );
  }

  const schedulePath = scheduleEvent
    ? scheduleEvent.path
    : schedulePathForYear(page.scheduleYear);

  const siteOrigin = parseHttpsUrl(site.origin, "site origin");
  if (
    siteOrigin.origin !== site.origin ||
    siteOrigin.pathname !== "/" ||
    siteOrigin.search ||
    siteOrigin.hash
  ) {
    throw new Error("Site origin must be an HTTPS origin without a path.");
  }

  const canonical = parseHttpsUrl(canonicalUrl, "schedule canonical URL");
  if (
    canonical.origin !== siteOrigin.origin ||
    canonical.pathname !== schedulePath ||
    canonical.search ||
    canonical.hash ||
    canonical.href !== `${siteOrigin.origin}${schedulePath}`
  ) {
    throw new Error(
      `Schedule canonical URL must match ${schedulePath}.`
    );
  }

  const artistId = parseHttpsUrl(site.artist.id, "schedule performer id");
  const artistUrl = parseHttpsUrl(site.artist.url, "schedule performer URL");
  if (
    artistId.origin !== siteOrigin.origin ||
    artistUrl.origin !== siteOrigin.origin
  ) {
    throw new Error("Schedule performer URLs must use the site origin.");
  }
  requireNonEmptyString(site.artist.name, "schedule performer name");

  const itemIds = new Set();
  const eventIds = new Set();
  const occurrences = [];
  const scheduleItemsForYear = scheduleItems
    .map((item, itemIndex) => {
      if (
        !item ||
        typeof item !== "object" ||
        !Array.isArray(item.occurrences)
      ) {
        throw new Error(`Invalid schedule item at position ${itemIndex + 1}.`);
      }

      const matchingOccurrences = item.occurrences.filter(
        (occurrence, occurrenceIndex) => {
          if (!occurrence || typeof occurrence !== "object") {
            throw new Error(
              `Invalid occurrence ${occurrenceIndex + 1} for ${item.id}.`
            );
          }

          const startDate = validateJstDateTime(
            occurrence.startDate,
            `start date for ${occurrence.id}`
          );

          return Number(startDate.slice(0, 4)) === page.scheduleYear;
        }
      );

      return matchingOccurrences.length
        ? { ...item, occurrences: matchingOccurrences }
        : null;
    })
    .filter(Boolean)
    .filter(
      (item) => !scheduleEvent || item.id === scheduleEvent.scheduleItemId
    );

  if (!scheduleItemsForYear.length) {
    throw new Error(
      `Schedule structured data requires an item for ${page.scheduleYear}.`
    );
  }

  scheduleItemsForYear.forEach((item, itemIndex) => {
    if (!item || typeof item !== "object") {
      throw new Error(`Invalid schedule item at position ${itemIndex + 1}.`);
    }
    if (!EVENT_ID_PATTERN.test(item.id)) {
      throw new Error(`Invalid schedule item id: ${item.id}`);
    }
    if (itemIds.has(item.id) || eventIds.has(item.id)) {
      throw new Error(`Duplicate schedule item id: ${item.id}`);
    }
    itemIds.add(item.id);

    const name = requireNonEmptyString(item.name, `name for ${item.id}`);
    const description = requireNonEmptyString(
      item.description,
      `description for ${item.id}`
    );
    const image = normalizeImages({
      imageKeys: item.imageKeys,
      imageUrlsByKey,
      itemId: item.id,
      siteOrigin,
    });
    const organizers = normalizeOrganizers(item.organizers, item.id);

    if (!Array.isArray(item.occurrences) || !item.occurrences.length) {
      throw new Error(`Schedule item ${item.id} requires an occurrence.`);
    }

    item.occurrences.forEach((occurrence, occurrenceIndex) => {
      if (!occurrence || typeof occurrence !== "object") {
        throw new Error(
          `Invalid occurrence ${occurrenceIndex + 1} for ${item.id}.`
        );
      }
      if (!EVENT_ID_PATTERN.test(occurrence.id)) {
        throw new Error(`Invalid schedule event id: ${occurrence.id}`);
      }
      if (eventIds.has(occurrence.id) || itemIds.has(occurrence.id)) {
        throw new Error(`Duplicate schedule event id: ${occurrence.id}`);
      }
      eventIds.add(occurrence.id);

      const startDate = validateJstDateTime(
        occurrence.startDate,
        `start date for ${occurrence.id}`
      );
      const doorTime = validateJstDateTime(
        occurrence.doorTime,
        `door time for ${occurrence.id}`
      );
      const endDate = occurrence.endDate === undefined
        ? null
        : validateJstDateTime(
            occurrence.endDate,
            `end date for ${occurrence.id}`
          );
      const eventStatus = requireNonEmptyString(
        occurrence.eventStatus,
        `event status for ${occurrence.id}`
      );

      if (
        doorTime.slice(0, 10) !== startDate.slice(0, 10) ||
        Date.parse(doorTime) > Date.parse(startDate)
      ) {
        throw new Error(`Invalid door time ordering for ${occurrence.id}.`);
      }
      if (eventStatus !== EVENT_SCHEDULED) {
        throw new Error(`Unsupported event status for ${occurrence.id}.`);
      }
      if (endDate && Date.parse(endDate) <= Date.parse(startDate)) {
        throw new Error(`Invalid end date ordering for ${occurrence.id}.`);
      }

      occurrences.push({
        scheduleItemId: item.id,
        id: occurrence.id,
        name,
        description,
        startDate,
        doorTime,
        endDate,
        eventStatus,
        image,
        location: validateLocation(occurrence.location, occurrence.id),
        offers: normalizeOffers({
          ticketOffers: item.ticketOffers,
          ticketUrl: occurrence.ticketUrl,
          eventId: occurrence.id,
          startDate,
        }),
        organizers,
      });
    });
  });

  occurrences.sort((left, right) => {
    const dateOrder = left.startDate.localeCompare(right.startDate);

    return dateOrder || left.id.localeCompare(right.id);
  });

  return { canonical: canonical.href, occurrences };
};

const createScheduleStructuredData = ({
  canonicalUrl,
  imageUrlsByKey,
  page,
  site,
  scheduleItems,
}) => {
  const { canonical, occurrences } = validateScheduleContext({
    canonicalUrl,
    imageUrlsByKey,
    page,
    site,
    scheduleItems,
  });
  const performer = {
    "@type": "Person",
    "@id": site.artist.id,
    name: site.artist.name,
    url: site.artist.url,
  };

  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${canonical}#webpage`,
    url: canonical,
    name: page.title,
    description: page.description,
    inLanguage: "ja-JP",
    mainEntity: {
      "@type": "ItemList",
      "@id": `${canonical}#schedule-events`,
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      numberOfItems: occurrences.length,
      itemListElement: occurrences.map((occurrence, index) => {
        const scheduleEvent = scheduleEventCatalogEntryForItemId(
          occurrence.scheduleItemId
        );
        const eventDocumentUrl = scheduleEvent
          ? new URL(scheduleEvent.path, canonical).href
          : canonical;
        const eventUrl = `${eventDocumentUrl}#${occurrence.id}`;
        const event = {
          "@type": "MusicEvent",
          "@id": eventUrl,
          url: eventUrl,
          name: occurrence.name,
          description: occurrence.description,
          startDate: occurrence.startDate,
          doorTime: occurrence.doorTime,
          eventStatus: occurrence.eventStatus,
          eventAttendanceMode: OFFLINE_EVENT_ATTENDANCE_MODE,
          image: occurrence.image,
          location: occurrence.location,
          performer,
        };

        if (occurrence.endDate) {
          event.endDate = occurrence.endDate;
        }
        if (occurrence.offers.length) {
          event.offers = occurrence.offers;
        }
        if (occurrence.organizers.length) {
          event.organizer =
            occurrence.organizers.length === 1
              ? occurrence.organizers[0]
              : occurrence.organizers;
        }

        return {
          "@type": "ListItem",
          position: index + 1,
          name: occurrence.name,
          url: eventUrl,
          item: event,
        };
      }),
    },
  };
};

const serializeScheduleStructuredData = (structuredData) => {
  if (!structuredData || typeof structuredData !== "object") {
    throw new Error("Schedule structured data must be an object.");
  }

  return JSON.stringify(structuredData)
    .replace(/</g, "\\u003c")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
};

module.exports = {
  createScheduleStructuredData,
  serializeScheduleStructuredData,
};
